declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

export function trackEvent(
  eventName: string,
  eventParams: Record<string, unknown> = {}
) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

export function trackPageView(url: string, title?: string) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: title,
      page_location: window.location.href,
    });
  }
}

export function trackConversion(conversionLabel: string, value?: number) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID}/${conversionLabel}`,
      value: value,
      currency: 'CAD',
    });
  }
}

export function trackLocationPageView(locationName: string) {
  trackEvent('location_page_view', {
    location: locationName,
    event_category: 'Location Pages',
    event_label: locationName,
  });
}

export function trackContactFormSubmit(source: string) {
  trackEvent('contact_form_submit', {
    event_category: 'Lead Generation',
    event_label: source,
  });
}

export function trackPhoneClick(source: string) {
  trackEvent('phone_click', {
    event_category: 'Lead Generation',
    event_label: source,
  });
}

// ── Conversion Funnel Tracking ──────────────────────────────────────────────

/**
 * Track when a user submits any lead-generation form.
 * @param formName  Human-readable form identifier, e.g. "Quote Request", "Contact Form"
 * @param source    Page or section the form lives on, e.g. "/quote", "/contact"
 */
export function trackFormSubmission(formName: string, source: string) {
  trackEvent('form_submission', {
    event_category: 'Conversion Funnel',
    event_label: formName,
    form_name: formName,
    page_source: source,
  });
}

/**
 * Track when a user selects a specific service (dropdown, radio, card click, etc.).
 * @param serviceName  The service selected, e.g. "Hood Cleaning Only"
 * @param source       Page or section where the selection happened
 */
export function trackServiceSelection(serviceName: string, source: string) {
  trackEvent('service_selection', {
    event_category: 'Conversion Funnel',
    event_label: serviceName,
    service_name: serviceName,
    page_source: source,
  });
}

/**
 * Track when a user reaches a booking confirmation state
 * (calendar shown, success message displayed, etc.).
 * @param bookingType  e.g. "New Customer - Free Inspection", "Returning Customer - Hood Cleaning" * @param source       Page path, e.g."/book-new", "/book-returning"
 */
export function trackBookingConfirmation(bookingType: string, source: string) {
  trackEvent('booking_confirmation', {
    event_category: 'Conversion Funnel',
    event_label: bookingType,
    booking_type: bookingType,
    page_source: source,
  });
}

/**
 * Fire a Google Ads "generate_lead" event so Google Ads detects the booking
 * page as a form submission / lead conversion (fixes "form not detected").
 * Also fires a gtag conversion event if NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID
 * and NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_LABEL are set.
 *
 * @param value     Optional monetary value of the lead (default 0)
 * @param currency  Currency code (default 'CAD')
 */
export function trackGoogleAdsLead(value = 0, currency = 'CAD') {
  if (typeof window === 'undefined' || !window.gtag) return;

  // 1. generate_lead — Google Ads recognises this as a form submission
  window.gtag('event', 'generate_lead', {
    currency,
    value,
    event_category: 'Lead Generation',
    event_label: 'Appointment Booked',
  });

  // 2. Explicit conversion event (requires Google Ads conversion tag in layout)
  const conversionId = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID;
  const conversionLabel = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_LABEL;
  if (conversionId && conversionLabel && !conversionLabel.startsWith('YOUR_')) {
    window.gtag('event', 'conversion', {
      send_to: `${conversionId}/${conversionLabel}`,
      value,
      currency,
      event_callback: () => {},
    });
  }
}

/**
 * Track when a user clicks a CTA that navigates to a booking page.
 * @param ctaLabel  Button/link text, e.g. "Book FREE Inspection"
 * @param source    Page where the CTA was clicked
 */
export function trackBookingCTAClick(ctaLabel: string, source: string) {
  trackEvent('booking_cta_click', {
    event_category: 'Conversion Funnel',
    event_label: ctaLabel,
    cta_label: ctaLabel,
    page_source: source,
  });
}

/**
 * Track scroll depth milestones (25, 50, 75, 100 percent).
 * @param depth     Percentage milestone reached, e.g. 25 | 50 | 75 | 100
 * @param source    Page path where scroll occurred, e.g. "/kitchen-exhaust-cleaning-costs"
 */
export function trackScrollDepth(depth: 25 | 50 | 75 | 100, source: string) {
  trackEvent('scroll_depth', {
    event_category: 'Engagement',
    event_label: `${depth}%`,
    scroll_depth_threshold: depth,
    page_source: source,
    non_interaction: depth < 50,
  });
}

/**
 * Track when a user first interacts with a lead-generation form (focus on any field).
 * @param formName  Human-readable form identifier, e.g. "Contact Form"
 * @param source    Page path where the form lives, e.g. "/contact"
 */
export function trackFormStart(formName: string, source: string) {
  trackEvent('form_start', {
    event_category: 'Conversion Funnel',
    event_label: formName,
    form_name: formName,
    page_source: source,
  });
}

// ── Google Ads Conversion Events ─────────────────────────────────────────────

export const GADS_CONVERSION_ID = 'AW-18183160648';

/**
 * Fire Google Ads conversion for contact/quote form submission.
 * Requires NEXT_PUBLIC_GADS_CONTACTS_LABEL env variable.
 */
export function trackGAdsContactFormConversion() {
  if (typeof window === 'undefined' || !window.gtag) return;
  const label = process.env.NEXT_PUBLIC_GADS_CONTACTS_LABEL;
  if (!label || label.startsWith('YOUR_')) return;
  window.gtag('event', 'conversion', {
    send_to: `${GADS_CONVERSION_ID}/${label}`,
    value: 1.0,
    currency: 'CAD',
  });
}

/**
 * Fire Google Ads conversion for a quote request submission.
 * Requires NEXT_PUBLIC_GADS_QUOTE_LABEL env variable.
 */
export function trackGAdsQuoteConversion() {
  if (typeof window === 'undefined' || !window.gtag) return;
  const label = process.env.NEXT_PUBLIC_GADS_QUOTE_LABEL;
  if (!label || label.startsWith('YOUR_')) return;
  window.gtag('event', 'conversion', {
    send_to: `${GADS_CONVERSION_ID}/${label}`,
  });
}

/**
 * Fire Google Ads conversion for phone call click.
 * Requires NEXT_PUBLIC_GADS_PHONE_LABEL env variable.
 */
export function trackGAdsPhoneCallConversion() {
  if (typeof window === 'undefined' || !window.gtag) return;
  const label = process.env.NEXT_PUBLIC_GADS_PHONE_LABEL;
  if (!label || label.startsWith('YOUR_')) return;
  window.gtag('event', 'conversion', {
    send_to: `${GADS_CONVERSION_ID}/${label}`,
  });
}
